import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useOutletContext } from 'react-router-dom'
import { getTablePageSize } from '@/hooks/useTablePageSize'
import {
  Box,
  CircularProgress,
} from '@mui/material'
import dayjs from 'dayjs'
import { useRangeLimits } from '@/components/DatePickers/useRangeLimits'
import type { Dayjs } from 'dayjs'
import DataGridPremiumDemo from '@/components/Table/DataGrid'
import NotificationsPanel from '@/components/ConsumerNotifications/NotificationsPanel'
import columnsConfig from '@/components/features/config/columns/notifications/notificationsGeneral.columns'
import {
  getAlertTypes,
  getAllConsumerNotifications,
  getAlertMessages,
  exportAllNotificationsExcel,
  AlertType,
  AllConsumerNotification,
} from '@/services/notificationService'
import { MessageItem } from '@/mockdata/messagesByUser'
import { useAppTheme } from '@/theme/ThemeContext'
import { useGeneralNotificationsStore } from '@/store/notification/general'
import MoreFiltersDialog, { MoreFilters, EMPTY_FILTERS } from '@/components/Notifications/MoreFiltersDialog'
import ShowClosedCheckbox from '@/components/Notifications/ShowClosedCheckbox'
import FilterToolbar, { type FilterToolbarField } from '@/components/common/FilterToolbar'
import { SeverityBucket, severityParamFromBucket } from '@/utils/severity'
import { describeNotificationChannel } from '@/services/notificationChannels'

const General: React.FC = () => {
  const accountId = localStorage.getItem('accountId') || ''
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'
  const { filteredGeneralNotifications, setFilteredGeneralNotifications } = useGeneralNotificationsStore()

  // Alert types from API
  const [alertTypes, setAlertTypes] = useState<AlertType[]>([])

  // Filter states
  const [showClosedResults, setShowClosedResults] = useState(false)
  const [fromDate, setFromDate] = useState<Dayjs | null>(dayjs().subtract(2, 'month'))
  const [toDate, setToDate] = useState<Dayjs | null>(dayjs())
  const [alertTypeFilter, setAlertTypeFilter] = useState<string | null>(null)
  const [severityFilter, setSeverityFilter] = useState<string | null>(null)
  const [moreOpen, setMoreOpen] = useState(false)
  const [moreFilters, setMoreFilters] = useState<MoreFilters>(EMPTY_FILTERS)

  // Values are the shared severity buckets; the raw engine integer
  // (Low=-10, Medium=0, High=10) is what actually goes over the wire.
  // '' is "all": severityParamFromBucket('') sends no severity filter.
  const severityOptions: { label: string; value: SeverityBucket | '' }[] = [
    { label: 'הכל', value: '' },
    { label: 'נמוכה', value: 'low' },
    { label: 'בינונית', value: 'medium' },
    { label: 'גבוהה', value: 'high' },
  ]

  const severityValue = severityParamFromBucket(severityFilter)

  // Data states
  const [rows, setRows] = useState<(AllConsumerNotification & { id: number })[]>([])
  const [loading, setLoading] = useState(true)
  const [totalRowCount, setTotalRowCount] = useState(0)
  const [currentPage, setCurrentPage] = useState(0) // 0-based for DataGrid
  const [pageSize, setPageSize] = useState(() => getTablePageSize(50))
  const [sortBy, setSortBy] = useState<string>('createdAt')
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC')

  // Messages panel
  const [selectedRowId, setSelectedRowId] = useState<string | number | null>(null)
  const [messages, setMessages] = useState<MessageItem[]>([])
  const [messagesLoading, setMessagesLoading] = useState(false)

  const fetchMessages = useCallback(async (alertId: number) => {
    if (!accountId) return
    setMessagesLoading(true)
    try {
      const data = await getAlertMessages(accountId, alertId)
      const mapped: MessageItem[] = data.map(msg => ({
        id: String(msg.notificationId),
        sender: msg.sender || '',
        channel: describeNotificationChannel(msg.channel).label,
        contact: msg.recipient || '',
        preview: msg.body || '',
        receivedAt: msg.sentAt
          ? dayjs(msg.sentAt).format('HH:mm DD/MM/YYYY')
          : dayjs(msg.createdAt).format('HH:mm DD/MM/YYYY'),
        status: msg.status,
        lastError: msg.lastError,
      }))
      setMessages(mapped)
    } finally {
      setMessagesLoading(false)
    }
  }, [accountId])

  const initialFetchDone = useRef(false)
  const shouldSelectFirstRowRef = useRef(false)

  // Outlet context for Excel export
  const outletContext = useOutletContext<{
    search?: string
    setExcelExportHandler?: (fn: (() => void) | null) => void
    setIsExporting?: (v: boolean) => void
    setExportProgress?: (v: number | ((prev: number) => number)) => void
  }>() ?? {}

  const progressTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const stopProgressTimer = useCallback(() => {
    if (progressTimerRef.current) {
      clearInterval(progressTimerRef.current)
      progressTimerRef.current = null
    }
  }, [])

  const handleExcelExport = useCallback(async () => {
    if (!accountId) return
    const { setIsExporting, setExportProgress } = outletContext
    if (!setIsExporting || !setExportProgress) return

    setIsExporting(true)
    setExportProgress(0)
    stopProgressTimer()

    let simulated = 0
    progressTimerRef.current = setInterval(() => {
      simulated += (90 - simulated) * 0.05
      setExportProgress((prev: number) => Math.min(Math.round(Math.max(prev, simulated)), 90))
    }, 300)

    const onProgress = (percent: number) => {
      setExportProgress((prev: number) => Math.max(prev, percent))
    }

    try {
      const startDate = (fromDate || dayjs().subtract(2, 'month')).format('YYYY-MM-DD')
      const endDate = (toDate || dayjs()).format('YYYY-MM-DD')

      await exportAllNotificationsExcel(accountId, {
        startDate,
        endDate,
        sortBy,
        sortOrder,
        includeClosed: showClosedResults,
        alertTypeId: alertTypeFilter ? Number(alertTypeFilter) : undefined,
        severity: severityValue,
        meterNumber: moreFilters.meterNumber ? Number(moreFilters.meterNumber) : undefined,
        consumerName: moreFilters.consumerName || undefined,
        waterCard: moreFilters.waterCard || undefined,
        alertName: moreFilters.alertName || undefined,
        openedFrom: moreFilters.openedFrom?.format('YYYY-MM-DD'),
        openedTo: moreFilters.openedTo?.format('YYYY-MM-DD'),
        closedFrom: moreFilters.closedFrom?.format('YYYY-MM-DD'),
        closedTo: moreFilters.closedTo?.format('YYYY-MM-DD'),
      }, onProgress)

      stopProgressTimer()
      setExportProgress(100)
      await new Promise(resolve => setTimeout(resolve, 500))
    } catch (err) {
      console.error('Excel export failed:', err)
    } finally {
      stopProgressTimer()
      setIsExporting(false)
      setExportProgress(0)
    }
  }, [accountId, fromDate, toDate, sortBy, sortOrder, showClosedResults, alertTypeFilter, severityValue, moreFilters, stopProgressTimer])

  // Register export handler with parent layout
  useEffect(() => {
    if (outletContext.setExcelExportHandler) {
      outletContext.setExcelExportHandler(() => handleExcelExport)
      return () => outletContext.setExcelExportHandler!(null)
    }
  }, [handleExcelExport])

  // Fetch alert types on mount
  const [typesLoaded, setTypesLoaded] = useState(false)
  useEffect(() => {
    if (!accountId) return
    const fetchTypes = async () => {
      try {
        const types = await getAlertTypes(accountId)
        setAlertTypes(types || [])
      } catch (err) {
        setAlertTypes([])
      } finally {
        setTypesLoaded(true)
      }
    }
    fetchTypes()
  }, [accountId])

  // Once alert types are loaded, apply store filter (from dashboard click) and do initial fetch
  useEffect(() => {
    if (!accountId || !typesLoaded || initialFetchDone.current) return
    initialFetchDone.current = true

    // Check if dashboard navigated with a specific alert type
    const storeItem = filteredGeneralNotifications[0]
    let initialAlertTypeId: string | null = null
    let initialFromDate: Dayjs | null = null
    if (storeItem?.id) {
      initialAlertTypeId = storeItem.id
      setAlertTypeFilter(initialAlertTypeId)
      // Widen date filter to 1 year ago so older open notifications are loaded
      initialFromDate = dayjs().subtract(1, 'year')
      setFromDate(initialFromDate)
      // Flag to select first row when data loads
      shouldSelectFirstRowRef.current = true
      // Clear store so it doesn't persist for the next visit
      setFilteredGeneralNotifications([])
    }

    // Fetch with the determined filter (pass directly since setState is async)
    fetchNotifications(initialAlertTypeId, undefined, undefined, undefined, undefined, initialFromDate)
  }, [typesLoaded, accountId])

  const fetchNotifications = useCallback(async (
    alertTypeIdOverride?: string | null,
    pageOverride?: number,
    pageSizeOverride?: number,
    sortByOverride?: string,
    sortOrderOverride?: 'ASC' | 'DESC',
    fromDateOverride?: Dayjs | null,
    moreFiltersOverride?: MoreFilters,
  ) => {
    if (!accountId) return
    setLoading(true)
    try {
      const effectiveFromDate = fromDateOverride !== undefined ? fromDateOverride : fromDate
      const startDate = (effectiveFromDate || dayjs().subtract(2, 'month')).format('YYYY-MM-DD')
      const endDate = (toDate || dayjs()).format('YYYY-MM-DD')

      const effectiveAlertTypeId = alertTypeIdOverride !== undefined ? alertTypeIdOverride : alertTypeFilter
      const apiPage = (pageOverride ?? currentPage) + 1 // API is 1-based
      const apiSize = pageSizeOverride ?? pageSize

      const filtersToUse = moreFiltersOverride !== undefined ? moreFiltersOverride : moreFilters

      const response = await getAllConsumerNotifications(accountId, {
        startDate,
        endDate,
        includeClosed: showClosedResults,
        alertTypeId: effectiveAlertTypeId
          ? Number(effectiveAlertTypeId)
          : undefined,
        page: apiPage,
        size: apiSize,
        sortBy: sortByOverride ?? sortBy,
        sortOrder: sortOrderOverride ?? sortOrder,
        severity: severityValue,
        meterNumber: filtersToUse.meterNumber
          ? Number(filtersToUse.meterNumber)
          : undefined,
        consumerName: filtersToUse.consumerName || undefined,
        waterCard: filtersToUse.waterCard || undefined,
        alertName: filtersToUse.alertName || undefined,
        openedFrom: filtersToUse.openedFrom?.format('YYYY-MM-DD'),
        openedTo: filtersToUse.openedTo?.format('YYYY-MM-DD'),
        closedFrom: filtersToUse.closedFrom?.format('YYYY-MM-DD'),
        closedTo: filtersToUse.closedTo?.format('YYYY-MM-DD'),
      })

      const mapped = (response.result || []).map(alert => ({
        ...alert,
        id: alert.alertId,
      }))
      setRows(mapped)
      setTotalRowCount(response.total ?? 0)

      if (shouldSelectFirstRowRef.current && mapped.length > 0) {
        shouldSelectFirstRowRef.current = false
        const firstRow = mapped[0]
        setSelectedRowId(firstRow.id)
        fetchMessages(firstRow.id)
      }
    } catch (error) {
      console.error('[NotificationGeneral] Failed to fetch notifications:', error)
    } finally {
      setLoading(false)
    }
  }, [accountId, fromDate, toDate, showClosedResults, alertTypeFilter, severityValue, moreFilters, currentPage, pageSize, sortBy, sortOrder, fetchMessages])

  const handleSearch = useCallback(() => {
    setSelectedRowId(null)
    setMessages([])
    setCurrentPage(0)
    fetchNotifications(undefined, 0)
  }, [fetchNotifications])

  const handleClearFilters = useCallback(() => {
    setFromDate(dayjs().subtract(2, 'month'))
    setToDate(dayjs())
    setAlertTypeFilter(null)
    setSeverityFilter(null)
    setShowClosedResults(false)
    setMoreFilters(EMPTY_FILTERS)
    setSelectedRowId(null)
    setMessages([])
    setCurrentPage(0)

    const startDate = dayjs().subtract(2, 'month').format('YYYY-MM-DD')
    const endDate = dayjs().format('YYYY-MM-DD')

    getAllConsumerNotifications(accountId, {
      startDate,
      endDate,
      includeClosed: false,
      page: 1,
      size: pageSize,
      sortBy,
      sortOrder,
    }).then(response => {
      const mapped = (response.result || []).map(alert => ({
        ...alert,
        id: alert.alertId,
      }))
      setRows(mapped)
      setTotalRowCount(response.total ?? 0)
    })
  }, [accountId, pageSize, sortBy, sortOrder])

  const handleApplyMoreFilters = useCallback((nextFilters: MoreFilters) => {
    setMoreFilters(nextFilters)
    setSelectedRowId(null)
    setMessages([])
    setCurrentPage(0)
    fetchNotifications(undefined, 0, undefined, undefined, undefined, undefined, nextFilters)
  }, [fetchNotifications])

  const handlePageChange = useCallback((newPage: number, newPageSize?: number) => {
    const size = newPageSize ?? pageSize
    if (newPageSize && newPageSize !== pageSize) {
      setPageSize(newPageSize)
    }
    setCurrentPage(newPage)
    fetchNotifications(undefined, newPage, size)
  }, [fetchNotifications, pageSize])

  const handleSortChange = useCallback((field: string, order: 'ASC' | 'DESC') => {
    setSortBy(field)
    setSortOrder(order)
    setCurrentPage(0)
    fetchNotifications(undefined, 0, undefined, field, order)
  }, [fetchNotifications])

  const handleRowClick = useCallback((row: any) => {
    setSelectedRowId(row.id)
    fetchMessages(row.id)
  }, [fetchMessages])

  const rowSelectionModel = useMemo(() => {
    return selectedRowId !== null
      ? { type: 'include' as const, ids: new Set([selectedRowId]) }
      : { type: 'include' as const, ids: new Set<string | number>() }
  }, [selectedRowId])

  const limits = useRangeLimits(
    { from: fromDate?.toDate() ?? null, to: toDate?.toDate() ?? null },
    {}
  )

  const filterFields: FilterToolbarField[] = [
    {
      name: 'alertType',
      type: 'select',
      label: 'סוג התראה',
      value: alertTypeFilter ?? '',
      options: [
        { label: 'הכל', value: '' },
        ...alertTypes.map(type => ({
          label: type.alertTypeName,
          value: String(type.alertTypeId),
        })),
      ],
      onChange: value => setAlertTypeFilter(value || null),
    },
    {
      name: 'severity',
      type: 'select',
      label: 'חומרה',
      value: severityFilter ?? '',
      options: severityOptions,
      onChange: value => setSeverityFilter(value || null),
    },
    {
      name: 'fromDate',
      type: 'date',
      label: 'מתאריך',
      value: fromDate?.toDate() ?? null,
      disableFuture: true,
      onChange: value => setFromDate(value ? dayjs(value) : null),
      ...limits.from,
    },
    {
      name: 'dateSeparator',
      type: 'custom',
      render: (
        <Box sx={{ width: 10, height: 2, bgcolor: isDarkMode ? '#BBD7FF' : '#003F61' }} />
      ),
    },
    {
      name: 'toDate',
      type: 'date',
      label: 'עד תאריך',
      value: toDate?.toDate() ?? null,
      disableFuture: true,
      onChange: value => setToDate(value ? dayjs(value) : null),
      ...limits.to,
    },
    {
      name: 'showClosed',
      type: 'custom',
      render: (
        <ShowClosedCheckbox
          checked={showClosedResults}
          onChange={setShowClosedResults}
        />
      ),
    },
  ]

  return (
    <Box
      sx={{
        width: '100%',
        alignItems: 'center',
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Filter bar */}
      <Box sx={{ width: '100%', mt: 'clamp(16px, 2vw, 36px)', px: 'clamp(8px, 1vw, 20px)' }}>
        <FilterToolbar
          fields={filterFields}
          onSearch={handleSearch}
          disabled={limits.hasError}
          onClear={alertTypeFilter ? handleClearFilters : undefined}
          clearLabel="נקה"
        />
      </Box>

      {/* Data grid + messages panel */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          justifyContent: 'space-between',
          width: '100%',
          mt: 'clamp(12px, 1.5vw, 24px)',
          px: 'clamp(8px, 1vw, 20px)',
          alignItems: 'stretch',
          flexWrap: { xs: 'wrap', lg: 'nowrap' },
          flex: 1,
          minHeight: 0,
        }}
      >
        <Box sx={{ flex: { xs: '1 1 100%', lg: '0 1 clamp(220px, 18vw, 360px)' }, minWidth: { xs: 0, lg: 220 }, maxWidth: { xs: '100%', lg: '28%' } }}>
          <NotificationsPanel
            selectedRow={selectedRowId ? { id: selectedRowId } : null}
            items={messages}
            loading={messagesLoading}
          />
        </Box>
        <Box sx={{ flex: '1 1 0', minWidth: 0, width: '100%' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <DataGridPremiumDemo
              width="100%"
              columns={columnsConfig}
              rows={rows}
              hideFooter
              headerPosition="bottom"
              pageSize={pageSize}
              paginationMode="server"
              serverSideRowCount={totalRowCount}
              serverSidePage={currentPage}
              onPaginationChange={handlePageChange}
              sortingMode="server"
              onSortChange={handleSortChange}
              externalSortModel={[{ field: sortBy, sort: sortOrder.toLowerCase() as 'asc' | 'desc' }]}
              onRowClick={handleRowClick}
              onRowDoubleClick={handleRowClick}
              rowSelectionModel={rowSelectionModel}
              maxHeight="100%"
            />
          )}
        </Box>
      </Box>

      <MoreFiltersDialog
        open={moreOpen}
        initial={moreFilters}
        onApply={handleApplyMoreFilters}
        onClose={() => setMoreOpen(false)}
      />
    </Box>
  )
}

export default General
